/* eslint-disable no-param-reassign */
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { Ajax } from '@roku-web-core/roku-ajax-client';

import { AppDeviceLeaf } from 'types/common/leaf';
import { aspenSockets, SignalingData } from 'modules/aspenSockets';
import { getDefaultMicStream, stopMicStream } from 'modules/userMedia';

export const formatIceServers = (servers: Array<RTCIceServer & { url: RTCIceServer['urls'] }>) =>
  servers.map((server) => ({ ...server, urls: server.url }));

export const isValidPeerConnection = (peerConnection: RTCPeerConnection | undefined): boolean => {
  if (!peerConnection) return false;

  const state = peerConnection.connectionState;
  return state !== 'failed' && state !== 'closed';
};

export const isRecoverablePeerConnection = (peerConnection: RTCPeerConnection | undefined): boolean => {
  if (!peerConnection) return false;

  const { connectionState, iceConnectionState } = peerConnection;

  return (
    connectionState === 'disconnected' &&
    ['disconnected', 'failed'].includes(iceConnectionState) &&
    (peerConnection.getConfiguration()?.iceServers?.length || 0) > 0 &&
    !peerConnection.offerInProgress
  );
};

export const isActivePeerConnection = (peerConnection: RTCPeerConnection | undefined): boolean => {
  if (!peerConnection) return false;

  const { connectionState } = peerConnection;
  return ['connecting', 'connected'].includes(connectionState);
};

interface FilterOptions {
  filterIceTrickleOption: boolean;
  filterTCPCandidates: boolean;
}

const DEFAULT_FILTER_OPTIONS: FilterOptions = {
  filterIceTrickleOption: true,
  filterTCPCandidates: false,
};

export const filterSDP = (
  sdp: string,
  { filterIceTrickleOption, filterTCPCandidates }: Partial<FilterOptions> = DEFAULT_FILTER_OPTIONS,
) => {
  const filters = [
    filterTCPCandidates && /^a=candidate:.*tcp.*$\r?\n/gm,
    filterIceTrickleOption && /^a=ice-options:trickle$\r?\n/gm,
  ].filter(Boolean);

  return filters.reduce((filteredSDP, pattern) => filteredSDP.replace(pattern as RegExp, ''), sdp);
};

export const createMediaStreamUsingTransceivers = (transceivers: Array<RTCRtpTransceiver>) => {
  const mediaStream = new MediaStream();

  transceivers.forEach((transceiver) => {
    if (transceiver.receiver.track) mediaStream.addTrack(transceiver.receiver.track);
    if (transceiver.sender.track) mediaStream.addTrack(transceiver.sender.track);
  });

  return mediaStream;
};

type MicState = 'on' | 'off';

export const toggleMicrophoneTrack = async (state: MicState, peerConnection: RTCPeerConnection, stopStream = true) => {
  const sendRecvTransceiver = peerConnection
    .getTransceivers()
    .find((transceiver) => transceiver.direction === 'sendrecv');
  if (!sendRecvTransceiver) {
    throw Error('No sendrecv transceiver found');
  }

  switch (state) {
    case 'on': {
      const micStream = await getDefaultMicStream();
      if (micStream) {
        sendRecvTransceiver.sender.replaceTrack(micStream.getAudioTracks()[0]);
      }
      break;
    }
    case 'off':
      sendRecvTransceiver.sender.replaceTrack(null);
      if (stopStream) stopMicStream();
      break;
    default:
  }
};

export type ErrorOrigin = 'signaling' | 'webrtc' | 'device';

/**
 * Formats error messages based on their origin for better debugging and logging
 * @param error - The error to format
 * @param origin - The origin of the error (signaling, webrtc, device)
 * @returns Formatted error message
 */
export const formatErrorMessage = (error: Error, origin?: ErrorOrigin): string => {
  if (!origin) return error.message;

  switch (origin) {
    case 'signaling':
      return `Signaling Error - ${error.message}`;
    case 'webrtc':
      return `WebRTC Error - ${error.message}`;
    case 'device':
      return `Device - ${error.message}`;
    default:
      return error.message;
  }
};

interface RTCToolsParams {
  baseUrl: string;
  leaf: AppDeviceLeaf;
}

export const RTCTools = ({ baseUrl, leaf }: RTCToolsParams) => {
  const { extractWebRTCSession, fetchStreamInfo } = aspenSockets({ baseUrl });
  const { id: deviceId, type: deviceType } = leaf;

  interface SendOfferViaHttpsParams {
    deviceIdOverride?: AppDeviceLeaf['id'];
    peerConnection: RTCPeerConnection;
    signaling: SignalingData;
  }

  const sendOfferViaHttps = async ({
    deviceIdOverride = deviceId,
    peerConnection,
    signaling: { signalingUrl, signalToken },
  }: SendOfferViaHttpsParams): Promise<string> => {
    const sdpOffer = peerConnection.localDescription?.sdp as string;
    const filteredSDPOffer = filterSDP(sdpOffer);

    try {
      // Store signaling start time
      peerConnection.timers.signalingStart = dayjs().valueOf();

      const response = await Ajax.request
        .post(`/${baseUrl}/api/v1/signaling`)
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${signalToken}`)
        .set('x-request-id', uuidv4())
        .send({ signalingUrl, remotePayload: { action: 'offer', deviceId: deviceIdOverride, sdp: filteredSDPOffer } });

      // return SDP answer
      return response.body.sdp;
    } finally {
      // Store signaling end time
      peerConnection.timers.signalingEnd = dayjs().valueOf();
    }
  };

  interface SendOfferParams {
    peerConnection: RTCPeerConnection;
  }

  const sendOffer = async ({ peerConnection }: SendOfferParams) => {
    const { connectionState, iceConnectionState, signalingState, offerInProgress } = peerConnection;
    const deviceState = leaf.state;
    const isCameraAvailable =
      deviceState?.online?.online && (!deviceState?.power || deviceState?.power?.power === 'on');

    if (
      ['checking', 'connected', 'closed'].includes(iceConnectionState) ||
      ['connecting', 'connected', 'closed'].includes(connectionState) ||
      signalingState === 'stable' ||
      offerInProgress ||
      !isCameraAvailable
    ) {
      return;
    }

    try {
      peerConnection.offerInProgress = true;
      const { deviceId: deviceIdFromStreamInfo, signaling } = await fetchStreamInfo({
        leafId: deviceId,
        leafType: deviceType,
      });

      // Add webrtcSessionId to peerConnection
      const webRTCSession = extractWebRTCSession(signaling.signalingUrl);
      peerConnection.sessionId = webRTCSession;

      const sdpAnswer = await sendOfferViaHttps({
        deviceIdOverride: deviceIdFromStreamInfo,
        peerConnection,
        signaling,
      });
      const filteredSDPAnswer = filterSDP(sdpAnswer);

      // Set remote answer to remote description
      await peerConnection.setRemoteDescription({ type: 'answer', sdp: filteredSDPAnswer });
    } finally {
      // eslint-disable-next-line no-param-reassign
      peerConnection.offerInProgress = false;
    }
  };

  return {
    sendOfferViaHttps,
    sendOffer,
  };
};
